import i18n from "i18next";

import { JSX, LazyExoticComponent, Suspense, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { redirect, useParams } from "react-router-dom";

import ContentLangTab from "@/components/gate/update-profile/ContentLangTab.tsx";
import InterfaceLangTab from "@/components/gate/update-profile/InterfaceLangTab.tsx";
import PicturesTab from "@/components/gate/update-profile/PicturesTab.tsx";
import PinCodeTab from "@/components/gate/update-profile/PinCodeTab.tsx";
import RecommendationsTab from "@/components/gate/update-profile/RecommendationsTab.tsx";
import UpdateFormTab from "@/components/gate/update-profile/UpdateFormTab.tsx";
import { UpdateProfileProps, UpdateProfileTab } from "@/components/gate/update-profile/types.ts";
import GroupTransition from "@/components/helpers/GroupTransition/GroupTransition.tsx";
import Loader from "@/components/helpers/Loader.tsx";

import { ProfilePicture } from "@/api/user/user.types.ts";
import { ROUTES } from "@/config/routes.ts";
import { useGetProfileDetail } from "@/hooks/api/user.hooks.ts";

export interface UpdateProfileForm {
  name: string;
  gender: "M" | "W";
  age: number;
  pin_code: string;
  content_all: boolean;
  content_uz: boolean;
  content_ru: boolean;
  interface_lang: string;
  selected_content?: string[];
  picture: ProfilePicture["picture"] | null;
  date_of_birth: string;
}

type TabComponentType =
  | ((props: UpdateProfileProps) => JSX.Element)
  | LazyExoticComponent<(props: UpdateProfileProps) => JSX.Element>;

const TabObj: Record<UpdateProfileTab, TabComponentType> = {
  [UpdateProfileTab.FORM]: UpdateFormTab,
  [UpdateProfileTab.PICTURES]: PicturesTab,
  [UpdateProfileTab.PIN_CODE]: PinCodeTab,
  [UpdateProfileTab.INTERFACE_LANG]: InterfaceLangTab,
  [UpdateProfileTab.CONTENT_LANG]: ContentLangTab,
  [UpdateProfileTab.RECOMMENDATIONS]: RecommendationsTab,
};

const UpdateProfilePage = (): JSX.Element => {
  const { id } = useParams() as { id: string };
  const { data: profile } = useGetProfileDetail(Number(id));

  const [tab, setTab] = useState<UpdateProfileTab>(UpdateProfileTab.FORM);

  const formMethods = useForm<UpdateProfileForm>({
    defaultValues: {
      name: profile.name,
      gender: profile.gender ?? "M",
      content_all: profile.content_all ?? false,
      content_ru: profile.content_ru ?? false,
      content_uz: profile.content_uz ?? false,
      interface_lang: profile.interface_lang ?? i18n.language,
      picture: profile.picture ?? null,
      date_of_birth: profile.date_of_birth ?? "",
    },
    mode: "onSubmit",
  });

  if (!profile) {
    redirect(ROUTES.GATE.PROFILES);
    return <></>;
  }

  const TabComponent = TabObj[tab];

  return (
    <FormProvider {...formMethods}>
      <GroupTransition<HTMLDivElement> transitionKey={tab}>
        {({ ref }) => (
          <div ref={ref} className={"w-full"}>
            <Suspense fallback={<Loader className={"min-h-screen"} />}>
              <TabComponent profile={profile} tab={tab} setTab={setTab} />
            </Suspense>
          </div>
        )}
      </GroupTransition>
    </FormProvider>
  );
};

export default UpdateProfilePage;
